import { debounce } from 'lodash';
import React from 'react';
import {
    CommunityMemberSupportListEntryDto,
    ZonedDateTimeRangeDto
} from '@api/ChallengeClient';
import { Combobox } from '@components/combobox';
import { FieldLabel } from '@components/textfield';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { Input } from '@components/ui/input';
import { Api } from '@core/api';
import { injectTSDI } from '@core/tsdi';
import {
    AttributionDayField,
    defaultAttributionDay,
    isDayInRange
} from './attribution-day-field';
import {
    ChallengeResultFormat,
    formatChallengeResult,
    storedValueDiffersFromDisplay,
    storedValueUnitLabel
} from './raw-value';

interface AddParticipantDialogProps {
    open: boolean;
    communityId?: number;
    resultFormat: ChallengeResultFormat;
    requiresDay?: boolean;
    dateTimeRange?: ZonedDateTimeRangeDto;
    loading?: boolean;
    onClose(): void;
    onSubmit(userId: string, value: number, day?: string): Promise<void>;
}

export function AddParticipantDialog({
    open,
    communityId,
    resultFormat,
    requiresDay,
    dateTimeRange,
    loading,
    onClose,
    onSubmit
}: AddParticipantDialogProps) {
    const { challengeClient } = injectTSDI(Api);
    const [options, setOptions] = React.useState<
        CommunityMemberSupportListEntryDto[]
    >([]);
    const [searching, setSearching] = React.useState(false);
    const [member, setMember] =
        React.useState<CommunityMemberSupportListEntryDto | null>(null);
    const [value, setValue] = React.useState('0');
    const [day, setDay] = React.useState('');
    const valueId = React.useId();

    React.useEffect(() => {
        if (!open) {
            setOptions([]);
            setMember(null);
            setValue('0');
            setDay('');
            return;
        }
        setDay(defaultAttributionDay(dateTimeRange));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const search = React.useCallback(
        debounce((term: string) => {
            if (!communityId) {
                return;
            }
            setSearching(true);
            challengeClient.support
                .communityMemberSupportControllerGetCommunityMembers({
                    communityId,
                    search: term || undefined,
                    size: 20,
                    page: 0
                })
                .then((result) => setOptions(result.data.content || []))
                .finally(() => setSearching(false));
        }, 300),
        [communityId]
    );

    React.useEffect(() => {
        if (open) {
            search('');
        }
    }, [open, search]);

    const enteredValue = Number(value);
    const invalid =
        !member ||
        value.trim() === '' ||
        Number.isNaN(enteredValue) ||
        enteredValue < 0 ||
        (Boolean(requiresDay) && !isDayInRange(day, dateTimeRange));

    return (
        <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
            <DialogContent className="sm:max-w-xs">
                <DialogHeader>
                    <DialogTitle>Add Participant</DialogTitle>
                </DialogHeader>
                <Combobox
                    label="Community member"
                    options={options}
                    value={member}
                    onChange={(next) => setMember(next)}
                    onInputChange={search}
                    getOptionLabel={(option) =>
                        `${option.name} (${option.userId})`
                    }
                    getOptionKey={(option) => option.userId}
                    loading={searching}
                />
                <div className="flex flex-col gap-1.5">
                    <FieldLabel
                        htmlFor={valueId}
                        label={`Starting result (${storedValueUnitLabel(
                            resultFormat
                        )})`}
                    />
                    <Input
                        id={valueId}
                        type="number"
                        min={0}
                        step="any"
                        value={value}
                        onChange={(event) => setValue(event.target.value)}
                    />
                    {storedValueDiffersFromDisplay(resultFormat) &&
                        !Number.isNaN(enteredValue) && (
                            <p className="text-xs text-muted-foreground">
                                {`Displays as ${formatChallengeResult(
                                    enteredValue,
                                    resultFormat
                                )}`}
                            </p>
                        )}
                </div>
                {requiresDay && (
                    <AttributionDayField
                        value={day}
                        range={dateTimeRange}
                        onChange={setDay}
                    />
                )}
                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={invalid}
                        loading={loading}
                        onClick={() => {
                            if (!member) {
                                return;
                            }
                            void onSubmit(
                                member.userId,
                                enteredValue,
                                requiresDay ? day : undefined
                            );
                        }}
                    >
                        Add
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
